import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { Reveal } from "@/components/Reveal";
import { SEO } from "@/components/SEO";
import { articleDetails } from "@/data/articleDetails";
import { resources } from "@/data/content";

const Article = () => {
  const { slug } = useParams();
  const article = resources.find((item) => item.slug === slug);
  const detail = articleDetails[slug];

  if (!article || !detail) {
    return <Navigate to="/resources" replace />;
  }

  const Icon = article.icon;

  const shareArticle = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: article.title, url });
      } else {
        await navigator.clipboard.writeText(url);
      }
      toast.success("Article link ready to share.");
    } catch (error) {
      if (error.name !== "AbortError") {
        toast.error("We could not share the article. Please copy the page link.");
      }
    }
  };

  return (
    <>
      <SEO title={`${article.title} | Bello Joy Consulting`} description={article.excerpt} />
      <article data-testid="article-page" className="px-6 pb-24 pt-36 md:pb-32 md:pt-44">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <Link to="/resources" data-testid="back-to-resources-link" className="mb-10 inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-secondary">
              <ArrowLeft size={17} /> Back to Resources
            </Link>
            <div className="mb-6 flex flex-wrap items-center gap-4">
              <span className="rounded-full bg-muted px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary/70">
                {article.category}
              </span>
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-secondary">{article.readTime}</span>
            </div>
            <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-full bg-secondary/20 text-primary">
              <Icon size={28} />
            </div>
            <h1 data-testid="article-title" className="text-balance text-4xl leading-tight tracking-tight text-primary md:text-6xl">
              {article.title}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">{article.excerpt}</p>
            <Button
              type="button"
              variant="outline"
              onClick={shareArticle}
              data-testid="share-article-button"
              className="mt-8 rounded-full border-primary/25 bg-transparent px-6 text-primary hover:bg-secondary/20"
            >
              Share this article <Share2 size={16} />
            </Button>
          </Reveal>

          <div className="mt-16 space-y-12" data-testid="article-content">
            {detail.sections.map((section, index) => (
              <Reveal key={section.heading} delay={index * 0.04} data-testid={`article-section-${index + 1}`}>
                <section className="border-t border-primary/15 pt-9">
                  <span className="font-serif text-4xl italic text-secondary">{String(index + 1).padStart(2, "0")}</span>
                  <h2 className="mt-4 text-3xl tracking-tight text-primary">{section.heading}</h2>
                  <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">{section.body}</p>
                  {section.bullets ? (
                    <div className="mt-6 grid gap-3">
                      {section.bullets.map((bullet) => (
                        <div key={bullet} className="flex items-start gap-3 text-primary/85">
                          <CheckCircle2 size={19} className="mt-1 shrink-0 text-secondary" />
                          <span>{bullet}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </section>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16 rounded-[2rem] bg-primary p-8 text-primary-foreground shadow-soft md:p-10 dark-grain relative overflow-hidden" data-testid="article-key-takeaway">
            <div className="relative z-10">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-secondary">Key takeaway</p>
              <p className="font-serif text-2xl leading-relaxed italic md:text-3xl">{detail.takeaway}</p>
              <p className="mt-6 text-xs leading-relaxed text-primary-foreground/55">
                This article is educational and is not legal, tax, medical, or individualized financial advice. Alicia can help you review your specific situation and coordinate with qualified professional advisors when needed.
              </p>
            </div>
          </Reveal>

          <Reveal className="mt-10 rounded-[2rem] border border-secondary/40 bg-secondary/20 p-8 md:p-10" data-testid="article-consultation-cta">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-primary/70">Ready for personal guidance?</p>
            <h2 className="text-3xl tracking-tight text-primary md:text-4xl">Bring your questions to Alicia.</h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
              A short conversation can turn general education into a clear next step for your family, business, or legacy.
            </p>
            <Button asChild className="mt-7 rounded-full bg-primary px-7 text-primary-foreground transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-primary/90">
              <Link to="/contact" data-testid="article-contact-button">Schedule a Consultation <ArrowRight size={17} /></Link>
            </Button>
          </Reveal>
        </div>
      </article>
    </>
  );
};

export default Article;
